<script setup>
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { TrashCan16 } from '@carbon/icons-vue';
import { useI18n } from 'vue-i18n';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from 'dashboard/components-next/polaris/ui/table';

defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  emptyStateMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['delete', 'navigate']);

// Rebuilt inside on the kit's PolicyTable (phase 9.8): a framed two-column list on the registry table, the name
// (a link to the inbox or agent, as before), the value, and a remove button per row; loading and empty say so.
const { t } = useI18n();

const handleDelete = itemId => {
  emit('delete', itemId);
};

const handleNavigate = item => {
  emit('navigate', item);
};
</script>

<template>
  <div
    class="rounded-polaris-lg border border-solid border-polaris-border"
    data-policy-table
    :aria-busy="isFetching"
  >
    <div v-if="isFetching" class="flex items-center justify-center p-6">
      <Spinner />
    </div>
    <p
      v-else-if="items.length === 0 && emptyStateMessage"
      class="p-6 text-center type-polaris-body text-polaris-muted-foreground"
    >
      {{ emptyStateMessage }}
    </p>
    <Table v-else>
      <TableBody>
        <TableRow v-for="item in items" :key="item.id">
          <TableCell class="w-2/5">
            <button
              type="button"
              class="group -ms-1.5 flex min-w-0 items-center gap-2 rounded-polaris-md px-1.5 py-1 hover:bg-polaris-accent"
              @click="handleNavigate(item)"
            >
              <Icon
                v-if="item.icon"
                :icon="item.icon"
                class="size-4 shrink-0 text-polaris-muted-foreground"
              />
              <Avatar
                v-else
                :src="item.avatarUrl"
                :name="item.name"
                :size="24"
                rounded-full
              />
              <span class="truncate type-polaris-body">{{ item.name }}</span>
              <Icon
                icon="i-lucide-external-link"
                class="size-3.5 shrink-0 text-polaris-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
              />
            </button>
          </TableCell>
          <TableCell
            class="max-w-0 truncate type-polaris-body text-polaris-muted-foreground"
            :title="item.email || item.phoneNumber"
          >
            {{ item.email || item.phoneNumber }}
          </TableCell>
          <TableCell class="w-12 text-end">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              :aria-label="t('SETTINGS_TABLE.DELETE')"
              @click="handleDelete(item.id)"
            >
              <TrashCan16 aria-hidden="true" />
            </Button>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</template>
